import React, { useState } from 'react';
import { Fade } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import SearchIconDetails from 'assets/svg/searchIconDetails.svg';

import { Table } from 'components/Tools/Table';
import { TableContainer } from 'app/WMS/styles/styles';
import {
  IInventoryDetailsTableProps,
  DetailInventoryData,
  iFilterDetailParams,
} from '../../../types';

const InventoryDetailsTable: React.FC<IInventoryDetailsTableProps> = ({
  rows,
  enableResevedButton,
  handleOpenCollectModal,
}) => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const [detailData, setDetailData] = useState<DetailInventoryData[]>(rows);

  const handleFilterDetailData = (params: iFilterDetailParams) => {
    const hasFilter = Object.values(params).some(value => value?.trim() !== '');

    if (!hasFilter) {
      setDetailData(rows);
      return;
    }

    const normalizedParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        value?.toLowerCase().trim(),
      ])
    );

    const filtered = rows.filter(item =>
      Object.entries(normalizedParams).every(([key, value]) => {
        if (!value) return true;
        const fieldValue = String(
          item[key as keyof typeof item] ?? ''
        ).toLowerCase();
        return fieldValue.includes(value);
      })
    );

    if (filtered.length === 0) {
      setDetailData(rows);
      enqueueSnackbar(t('screens.receivement.resultsNotFound'), {
        variant: 'warning',
      });
      return;
    }

    enqueueSnackbar(
      `${filtered.length} ${t('screens.receivement.successFilter')}`,
      {
        variant: 'success',
      }
    );

    setDetailData(filtered);
  };

  return (
    <Fade in timeout={1000} unmountOnExit>
      <TableContainer>
        <Table<DetailInventoryData>
          filtersDestails
          hendleSearchDetail={(data: iFilterDetailParams) => {
            handleFilterDetailData(data);
          }}
          inputsPopUpFilter={[
            {
              name: 'descricaoEndereco',
              label: String(t('screens.reportsStock.address')),
              type: 'text',
              placeholder: String(t('screens.reportsStock.address')),
            },
            {
              name: 'descricaoPalete',
              type: 'text',
              label: String(t('screens.commom.pallet')),
              placeholder: String(t('screens.commom.pallet')),
            },
            {
              name: 'serie',
              type: 'text',
              label: String(t('screens.receivement.series')),
              placeholder: String(t('screens.receivement.series')),
            },
            {
              name: 'codigoPedido',
              type: 'text',
              label: String(t('screens.reportsStock.codeRequest')),
              placeholder: String(t('screens.reportsStock.codeRequest')),
            },
            {
              name: 'ean',
              type: 'text',
              label: 'EAN',
              placeholder: 'EAN',
            },
            {
              name: 'lote',
              type: 'text',
              label: String(t('screens.pickingConference.bathModalTitle')),
              placeholder: String(
                t('screens.pickingConference.bathModalTitle')
              ),
            },
            {
              name: 'validade',
              type: 'date',
              label: String(t('screens.pickingConference.validity')),
              placeholder: String(t('screens.pickingConference.validity')),
            },
          ]}
          columns={[
            {
              title: String(t('screens.reportsStock.address')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['descricaoEndereco'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.commom.pallet')),
              type: 'string',
              orderable: true,
              display: 'notEmpty',
              props: ['descricaoPalete'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },

            {
              title: String(t('screens.commom.box')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['caixa'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.reportsStock.codeRequest')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['codigoPedido'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: 'EAN',
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['ean'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.commom.product')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['produto'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.reportsStock.descriptionProduct')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['descricaoProduto'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.pickingConference.bathModalTitle')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['lote'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.commom.unitMeasure')),
              orderable: true,
              type: 'string',
              props: ['unidadeMedida'],
              display: 'notEmpty',
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.pickingConference.validity')),
              orderable: true,
              display: 'notEmpty',
              type: 'date',
              props: ['validade'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.receivement.series')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['serie'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.commom.color')),
              type: 'string',
              orderable: true,
              display: 'notEmpty',
              props: ['cor'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.commom.size')),
              type: 'string',
              orderable: true,
              display: 'notEmpty',
              props: ['tamanho'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.commom.stock')),
              type: 'string',
              orderable: true,
              display: 'notEmpty',
              props: ['quantidade'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.reportsStock.reserved')),
              orderable: true,
              type: 'string',
              display: enableResevedButton !== 'S' ? 'notEmpty' : undefined,
              props: ['reservado'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
              renderItem: row => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      marginRight: '10px',
                    }}
                    className="row-actions-button-detail"
                  >
                    {enableResevedButton === 'S' ? (
                      <>
                        <button
                          type="button"
                          className="action"
                          onClick={() => handleOpenCollectModal(row)}
                          aria-label="Editar"
                        >
                          <img
                            src={SearchIconDetails}
                            alt="Detalhes"
                            style={{ width: '30px' }}
                          />
                          <p className="hover-item">Pedidos</p>
                        </button>

                        <p>{row?.reservado || ''}</p>
                      </>
                    ) : (
                      <p style={{ marginLeft: '10px' }}>
                        {row?.reservado || ''}
                      </p>
                    )}
                  </div>
                );
              },
            },
            {
              title: String(t('screens.reportsStock.available')),
              orderable: true,
              display: 'notEmpty',
              type: 'string',
              props: ['disponivel'],
              cssProps: {
                width: '1%',
              },
              cssTitle: {
                width: '1%',
              },
            },
            {
              title: String(t('screens.reportsStock.brand')),
              type: 'string',
              display: 'notEmpty',
              orderable: true,
              props: ['descricaoMarca'],
              cssTitle: {
                width: '1%',
              },
              cssProps: {
                width: '1%',
              },
            },
          ]}
          rows={detailData}
          exportList
        />
      </TableContainer>
    </Fade>
  );
};
export default InventoryDetailsTable;
